import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useKeepAliveContext } from "@/hooks/use-keep-alive-context";
import {
  buildPathFromConfigPath,
  getFacilityContext,
  getPortfolioContext,
  getLegacyLeaf,
} from "@/utils/route-context";
import "./style.css";

export const H5DetailBackToListContext = React.createContext({
  backButton: null,
  title: "",
  registerDetailTabs: null,
  registerHeaderRight: null,
});

const DETAIL_LIST_OVERRIDES = {
  "workorder-detail": "workorder",
  "equipment-detail": "equipment",
  "project-detail": "project",
  "pm-plan-detail": "pm-plan",
  "facility-detail": "facility",
  "company-detail": "company",
  "elec-detail": "electricity",
  "gas-detail": "gas",
  "water-detail": "water",
  "forms-detail": "forms",
  "functional-testing-detail": "functional-testing",
  "equipment-category-detail": "equipment-category",
  "service-category-detail": "service-category",
  "service-request-detail": "service-request",
  "user-detail": "user-management",
};

const CREATE_LIST_OVERRIDES = {
  "create-workorder": "workorder",
  "create-equipment": "equipment",
  "create-project": "project",
  "create-pm-plan": "pm-plan",
  "create-facility": "facility",
  "create-company": "company",
  "create-electricity": "electricity",
  "create-gas": "gas",
  "create-water": "water",
  "create-form": "forms",
  "create-functional-testing": "functional-testing",
  "create-equipment-category": "equipment-category",
  "create-service-category": "service-category",
  "create-service-request": "service-request",
  "create-user": "user-management",
  "invite-user": "user-management",
};

export const getListLeafFromLegacyPath = (legacyPath = "", viewType) => {
  const contextualLeaf = getLegacyLeaf(legacyPath, viewType);
  const normalizedPath = String(legacyPath || "").replace(/^\/+|\/+$/g, "");
  const viewPrefix = `${viewType}/`;
  const leaf = contextualLeaf || (
    normalizedPath.startsWith(viewPrefix)
      ? normalizedPath.slice(viewPrefix.length)
      : ""
  );
  const firstSegment = leaf.split("/")[0];
  if (!firstSegment) return null;

  if (firstSegment.startsWith("create-")) {
    return CREATE_LIST_OVERRIDES[firstSegment] || firstSegment.replace(/^create-/, "");
  }

  if (firstSegment.endsWith("-detail")) {
    return DETAIL_LIST_OVERRIDES[firstSegment] || firstSegment.replace(/-detail$/, "");
  }

  return null;
};

export const isH5DetailRoute = (legacyPath = "", viewType) =>
  Boolean(getListLeafFromLegacyPath(legacyPath, viewType));

const useIsH5Viewport = () => {
  const reduxIsPC = useSelector((state) => state.App?.isPC);
  const getViewportIsH5 = () =>
    typeof window !== "undefined" ? window.innerWidth <= 767 : reduxIsPC === false;
  const [isH5, setIsH5] = useState(() => reduxIsPC === false || getViewportIsH5());

  useEffect(() => {
    const onResize = () => setIsH5(reduxIsPC === false || getViewportIsH5());
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [reduxIsPC]);

  return isH5;
};

const H5DetailBackToList = ({ children, route, viewType }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const state = useSelector((currentState) => currentState);
  const isH5 = useIsH5Viewport();
  const { closeTab, refreshTab } = useKeepAliveContext();
  const [headerRight, setHeaderRight] = useState(null);
  const title = route?.title || "";

  const listPath = useMemo(() => {
    const listLeaf = getListLeafFromLegacyPath(route?.legacyPath, viewType);
    if (!listLeaf) return null;

    if (viewType === "facility") {
      const context = getFacilityContext({
        ...state,
        companyId: params.companyId,
        facilityId: params.facilityId,
      });
      const path = buildPathFromConfigPath(`/facility/${listLeaf}`, viewType, context);
      return listLeaf === "service-request" ? `${path}${location.search || ""}` : path;
    }

    if (viewType === "portfolio") {
      const context = getPortfolioContext({
        ...state,
        companyId: params.companyId,
      });
      return buildPathFromConfigPath(`/portfolio/${listLeaf}`, viewType, context);
    }

    if (viewType === "admin") {
      return `/admin/${listLeaf}`;
    }

    if (viewType === "tennant") {
      return `/tennant/${listLeaf}${location.search || ""}`;
    }

    return null;
  }, [location.search, params.companyId, params.facilityId, route?.legacyPath, state, viewType]);

  const handleBack = useCallback(() => {
    if (!listPath) return;
    refreshTab(listPath);
    closeTab();
    navigate(listPath);
  }, [closeTab, listPath, navigate, refreshTab]);

  const backButton = useMemo(
    () => (
      <Button
        type="text"
        className="h5-detail-back-to-list-button"
        icon={<ArrowLeftOutlined />}
        aria-label="Back"
        onClick={handleBack}
      />
    ),
    [handleBack]
  );

  const registerDetailTabs = useCallback(() => {
    return () => {};
  }, []);

  const registerHeaderRight = useCallback((node) => {
    setHeaderRight(node);
    return () => setHeaderRight(null);
  }, []);

  if (!isH5 || !listPath) {
    return children;
  }

  const hasHeaderRight = Boolean(headerRight);
  const headerClassName = `h5-detail-header${hasHeaderRight ? " h5-detail-header--has-right-action" : ""}`;

  return (
    <H5DetailBackToListContext.Provider
      value={{
        backButton,
        title,
        registerDetailTabs,
        registerHeaderRight,
      }}
    >
      <div className={headerClassName}>
        <div className="h5-detail-header__left">{backButton}</div>
        <div className="h5-detail-header__title" title={title}>
          {title}
        </div>
        <div className="h5-detail-header__right">
          {headerRight || <span className="h5-detail-header__right-placeholder" />}
        </div>
      </div>
      <div className="h5-detail-page-content">{children}</div>
    </H5DetailBackToListContext.Provider>
  );
};

export default H5DetailBackToList;
